import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { ClientApiProps, AlphaTagPicker, queryToFilters } from "../../common";
import {
  Button,
  ButtonProps,
  ErrorScope,
  Group,
  IconButton,
  Label,
  Modal,
  Spinner,
  TextInput,
  ValidationHint,
  useValidatedState,
} from "acma-spa";
import {
  AuthorisationPayload,
  Client,
  Hint,
  IAlphaTag,
  getApiClient,
} from "../../api";
import { AuthorisationStore } from "../../stores/AuthorisationStore";
import { AlphaTagStore } from "../../stores/AlphaTagStore";
import { notEmpty } from "../../stores/ListCache";
import * as yup from "yup";
import { faClipboard } from "@fortawesome/free-solid-svg-icons";
import toast, { Toaster } from "react-hot-toast";

export interface IssueTokenModalProps extends ClientApiProps {
  open: boolean;
  onClose: () => any;
  onChange: () => Promise<any>;
}

interface Model {
  partyName: string;
  alphaTags: IAlphaTag[];
}

const schema: yup.ObjectSchema<Model> = yup.object({
  partyName: yup.string().required().label("SMS Sender"),
  alphaTags: yup.array().defined().min(1).max(10).label("Alpha tags"),
});

const notify = () => toast("Token copied to clipboard");

export const IssueTokenModal: React.FC<IssueTokenModalProps> = (props) => {
  const [error, setError] = useState<string | undefined>(undefined);
  const [spinner, setSpinner] = useState<string>("");
  const [defaultOptions, setDefaultOptions] = useState<IAlphaTag[]>([]);
  const [token, setToken] = useState("");

  const [model, setModel, errors, validate] = useValidatedState<Model>(
    {
      partyName: "",
      alphaTags: [],
    },
    schema
  );

  const location = useLocation();

  const load = async (client: Client) => {
    const filters = queryToFilters(location.search).filter(
      (r) => r.hint == Hint.Name
    );

    if (filters.length == 0) {
      return;
    }

    setSpinner("Loading...");
    try {
      const store = new AlphaTagStore(client);
      const alphaTagList = await store.getAlphaTags(
        undefined,
        filters.map((r) => r.value).filter(notEmpty),
        undefined,
        undefined,
        Hint.Name,
        false,
        1,
        100,
        []
      );
      const groups: Record<string, IAlphaTag[]> = {};
      const newDefaultOptions: IAlphaTag[] = [];

      for (var alphaTag of alphaTagList) {
        if (!alphaTag.name) {
          continue;
        }
        if (!(alphaTag.name in groups)) {
          groups[alphaTag.name] = [];
        }
        groups[alphaTag.name].push(alphaTag);
        newDefaultOptions.push(alphaTag);
      }

      const newAlphaTags: IAlphaTag[] = [];
      for (var name of Object.keys(groups)) {
        if (groups[name].length == 1) {
          newAlphaTags.push(groups[name][0]);
        }
      }

      setModel({ alphaTags: newAlphaTags }, ValidationHint.NoValidation);
      setDefaultOptions(newDefaultOptions);
    } finally {
      setSpinner("");
    }
  };

  useEffect(() => {
    const [client, abort] = getApiClient(
      props.baseUrl,
      props.b2cDomainName,
      props.msalClient
    );

    load(client);

    return function () {
      abort.abort();
    };
  }, []);

  const onAdd = async () => {
    var res = await validate("#issueTokenContainer");
    if (!res) {
      return;
    }

    const [client, abort] = getApiClient(
      props.baseUrl,
      props.b2cDomainName,
      props.msalClient
    );
    const store = new AuthorisationStore(client);

    setSpinner("Saving...");
    setError("");

    try {
      var token = await store.addAuthorisations(
        new AuthorisationPayload({
          partyName: model.partyName,
          alphaTagIds: model.alphaTags
            .map((r) => r.alphaTagId)
            .filter(notEmpty),
        })
      );
      if (token) {
        await props.onChange();
        setToken(token);
      } else {
        props.onClose();
      }
    } catch (e: any) {
      setError(e.message ? e.message : "Unexpected error");
    } finally {
      setSpinner("");
    }
  };

  var buttons: ButtonProps[];

  if (!token) {
    buttons = [
      {
        label: "Generate token",
        id: "getToken",
        onClick: () => onAdd(),
        disabled: spinner ? true : false,
      },
      {
        label: "Cancel",
        id: "cancel",
        onClick: () => props.onClose(),
        mode: "secondary",
      },
    ];
  } else {
    buttons = [
      {
        label: "Close",
        id: "close",
        onClick: () => props.onClose(),
        mode: "secondary",
      },
    ];
  }

  return (
    <>
      <Toaster position="top-center" reverseOrder={false} />
      {props.open ? (
        <Modal
          id="issueTokenModal"
          size="lg"
          open={true}
          onClose={() => !spinner && props.onClose()}
          title={"Issue confirmation"}
          onOpened={() => document.getElementById("partyName")?.focus()}
          leftButtons={buttons}
          keyboard={false}
          backdrop="static"
        >
          <div id="issueTokenContainer" className="mb-2">
            <Spinner isLoading={spinner ? true : false} loadingText={spinner}>
              <p>Add the SMS sender and alpha tags you want to issue confirmation for. Generate a token to send to the SMS sender to verify your confirmation.</p>
              <Label
                id="partyName"
                label="SMS sender"
                hint="Enter the name of the SMS sender you want to issue confirmation to"
                error={errors.partyName}
                required
              >
                <TextInput
                  value={model.partyName}
                  onChange={(v) => setModel({ partyName: v })}
                  readonly={token ? true : false}
                  maxLength={400}
                />
              </Label>
              <Label
                id="alphaTags"
                label="Alpha tag(s)"
                hint="Alpha tags you are including in the confirmation"
                error={errors.alphaTags as string}
                required
                className="mt-3"
              >
                <AlphaTagPicker
                  {...props}
                  value={model.alphaTags}
                  onChange={(v) => setModel({ alphaTags: v })}
                  defaultOptions={defaultOptions}
                  disabled={token ? true : false}
                />
              </Label>
              {token ? (
                <Label
                  id="token"
                  label="Token"
                  hint="You must provide this token to the SMS sender. They will verify the token and confirm your alpha tag"
                  className="mt-3"
                >
                  <Group
                    items={[
                      {
                        element: (
                          <TextInput
                            value={token}
                            onChange={() => {}}
                            readonly
                          />
                        ),
                        flex: 2,
                      },
                      {
                        element: (
                          <Button
                            id="copy"
                            label={"Copy"}
                            title="copy"
                            onClick={() => {
                              navigator.clipboard.writeText(token);
                              notify();
                            }}
                          />
                        ),
                      },
                    ]}
                  />
                </Label>
              ) : (
                <></>
              )}
            </Spinner>
          </div>
          <ErrorScope id="error" error={error} />
        </Modal>
      ) : (
        <></>
      )}
    </>
  );
};
